import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ApiEndpoints } from '../../core/api-endpoints.service';
import {
  DEFAULT_LOCKED_SPACE,
  LockedSpace,
  WorkflowState,
} from '../../core/workflow-state.service';
import { ApiCallState } from '../../shared/api-call-state';
import {
  ApiEndpointComponent,
  ApiResultComponent,
} from '../../shared/api-result.component';
import { epochSecondsFromNow } from '../../shared/date-utils';

interface DeliveryOptionsResponse {
  lockedspaces: LockedSpace[];
  [key: string]: unknown;
}

interface DeliveryOptionsForm {
  email: string;
  phone: string;
  streetAddress: string;
  streetNumber: string;
  city: string;
  country: string;
  postalCode: string;
  latitude: number;
  longitude: number;
  lockId: string;
  capacityType: string;
  length: number;
  width: number;
  height: number;
  forAvailabilityCheck: boolean;
}

@Component({
  selector: 'app-delivery-options',
  imports: [FormsModule, ApiEndpointComponent, ApiResultComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './delivery-options.component.html',
})
export class DeliveryOptionsComponent {
  private readonly http = inject(HttpClient);
  private readonly endpoints = inject(ApiEndpoints);
  private readonly workflow = inject(WorkflowState);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly endpoint = this.endpoints.url(this.endpoints.deliveryOptions);
  protected readonly call = new ApiCallState();
  protected readonly lockedSpaces = signal<LockedSpace[]>([]);
  protected readonly model: DeliveryOptionsForm = {
    email: String(DEFAULT_LOCKED_SPACE['email'] ?? ''),
    phone: String(DEFAULT_LOCKED_SPACE['phoneNumber'] ?? ''),
    streetAddress: String(DEFAULT_LOCKED_SPACE['streetAddress'] ?? ''),
    streetNumber: String(DEFAULT_LOCKED_SPACE['streetNumber'] ?? ''),
    city: String(DEFAULT_LOCKED_SPACE['city'] ?? ''),
    country: String(DEFAULT_LOCKED_SPACE['country'] ?? 'SE'),
    postalCode: String(DEFAULT_LOCKED_SPACE['postalCode'] ?? ''),
    latitude: Number(DEFAULT_LOCKED_SPACE['latitude'] ?? 0),
    longitude: Number(DEFAULT_LOCKED_SPACE['longitude'] ?? 0),
    lockId: '',
    capacityType: 'DELIVERY',
    length: Number(DEFAULT_LOCKED_SPACE['length'] ?? 10),
    width: Number(DEFAULT_LOCKED_SPACE['width'] ?? 5),
    height: Number(DEFAULT_LOCKED_SPACE['height'] ?? 7),
    forAvailabilityCheck: false,
  };

  protected submit(): void {
    const payload = {
      forAvailabilityCheck: this.model.forAvailabilityCheck,
      deliveryInstruction: {
        lockOwnerId: {
          emailAddress: this.model.email || undefined,
          phoneNumber: this.model.phone || undefined,
        },
        deliveryAddress: {
          streetAddress: this.model.streetAddress,
          streetNumber: this.model.streetNumber,
          postalCode: this.model.postalCode,
          city: this.model.city,
          country: this.model.country,
          latitude: this.model.latitude,
          longitude: this.model.longitude,
          lockId: this.model.lockId || undefined,
          capacityType: this.model.capacityType || undefined,
        },
        deliveryItem: {
          id: 'string',
          description: 'string',
          dimension: {
            length: this.model.length,
            width: this.model.width,
            height: this.model.height,
          },
          weight: { weight: 0 },
          characteristics: {
            temperatureControl: { rangeMinimum: 0, rangeMaximum: 0 },
            misc: 'string',
          },
        },
      },
      ttl: epochSecondsFromNow(5),
    };

    this.call.start(payload);
    this.lockedSpaces.set([]);

    this.http
      .post<DeliveryOptionsResponse>(this.endpoint, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const items = Array.isArray(response.lockedspaces) ? response.lockedspaces : [];
          const metadata: LockedSpace = {
            shipperName: DEFAULT_LOCKED_SPACE['shipperName'],
            requesterId: DEFAULT_LOCKED_SPACE['requesterId'],
            email: this.model.email,
            phoneNumber: this.model.phone,
            streetAddress: this.model.streetAddress,
            streetNumber: this.model.streetNumber,
            postalCode: this.model.postalCode,
            city: this.model.city,
            country: this.model.country,
            latitude: this.model.latitude,
            longitude: this.model.longitude,
            length: this.model.length,
            width: this.model.width,
            height: this.model.height,
          };

          this.workflow.clearPendingAccessRequests();
          this.workflow.replaceLockedSpaces(items, metadata);
          this.lockedSpaces.set(items);
          this.call.succeed(response);
        },
        error: (error: unknown) => this.call.fail(error),
      });
  }
}